import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Btn, DataSizeBar, fmtBytes, fmtNumber, Section, StatCard, TypeBadge } from './parts';
import { ChunkTable, Chunk } from './ChunkTable';
import { useConfirmedMutation } from '@/api/mutations';
import { chunkOptimizeFn } from '@/api/endpoints';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';

export type HypertableObj = {
  schema: string;
  name: string;
  type: 'hypertable';
  on_disk_bytes: number;
  decompressed_bytes: number;
  compression_pct: number;
  rows: number;
  chunks_count: number;
  chunk_interval: string;
  policies?: string[];
};

const TARGET_DEFAULT = 256;
const TARGET_MIN = 128;
const TARGET_MAX = 1024;

export function DetailHypertable({ obj, chunks }: { obj: HypertableObj; chunks: Chunk[] }) {
  const fqn = `${obj.schema}.${obj.name}`;
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superAdmin';
  const qc = useQueryClient();
  const toast = useToast();
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [targetMb, setTargetMb] = useState(TARGET_DEFAULT);
  const targetValid = Number.isInteger(targetMb) && targetMb >= TARGET_MIN && targetMb <= TARGET_MAX;

  const optimizeMut = useConfirmedMutation(
    (mb: number) => chunkOptimizeFn(obj.name, mb),
    {
      title: `Optimiser les chunks de ${fqn}`,
      message: `Fusion/division des chunks vers ~${targetMb} Mo. Tâche mise en file (queue ingest), exécutée en série avec l'ingestion. Confirmer ?`,
      onSuccess: () => {
        toast.push('Optimisation des chunks mise en file', 'success');
        setOptimizeOpen(false);
        qc.invalidateQueries({ queryKey: ['db'] });
      },
    },
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/db" className="text-sm text-muted-foreground hover:text-foreground">
              ← Retour
            </Link>
            <h2 className="text-xl font-bold font-mono">{fqn}</h2>
            <TypeBadge type={obj.type} />
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Btn size="sm" variant="primary" onClick={() => setOptimizeOpen(true)}>
                Optimize chunks
              </Btn>
            )}
            <span className="text-xs text-muted-foreground">VACUUM via « Actions globales » (queue ingest)</span>
          </div>
        </div>

        <Section title="Vue d'ensemble">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="On-disk" value={fmtBytes(obj.on_disk_bytes)} tone="primary" sub="∑ chunks (table+index)" />
            <StatCard label="Décompressé" value={fmtBytes(obj.decompressed_bytes)} sub="taille originale" />
            <StatCard label="Compression" value={`${obj.compression_pct}%`} tone="success" sub="moyenne" />
            <StatCard label="Lignes" value={fmtNumber(obj.rows)} sub="approximatif" />
            <StatCard label="Chunks" value={obj.chunks_count} sub={`intervalle ${obj.chunk_interval}`} />
            <StatCard label="Policies" value={obj.policies?.length ?? 0} sub={obj.policies?.join(' · ')} />
          </div>
        </Section>

        <Section title="Comparaison taille">
          <DataSizeBar originalBytes={obj.decompressed_bytes} onDiskBytes={obj.on_disk_bytes} />
        </Section>

        <Section title={`Chunks (${chunks.length})`}>
          <ChunkTable chunks={chunks} hypertable={fqn} />
        </Section>
      </div>

      {optimizeOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50" onClick={() => setOptimizeOpen(false)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-bold">Optimiser les chunks</h3>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{fqn}</p>
            </div>
            <div className="px-5 py-4 space-y-2">
              <label className="text-sm font-medium" htmlFor="optimize-target-mb">Taille cible par chunk (Mo)</label>
              <input
                id="optimize-target-mb"
                type="number"
                min={TARGET_MIN}
                max={TARGET_MAX}
                step={64}
                value={targetMb}
                onChange={(e) => setTargetMb(Math.trunc(Number(e.target.value)))}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Les chunks seront fusionnés/divisés pour viser ~{targetMb} Mo. Bornes {TARGET_MIN}–{TARGET_MAX} Mo.
                Tâche exécutée dans la file ingest (en série avec l'ingestion).
              </p>
              {!targetValid && (
                <p className="text-xs text-destructive">Valeur hors bornes ({TARGET_MIN}–{TARGET_MAX} Mo, entier).</p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Btn onClick={() => setOptimizeOpen(false)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => optimizeMut.mutate(targetMb)} disabled={!targetValid || optimizeMut.isPending}>
                {optimizeMut.isPending ? 'Envoi…' : 'Optimiser (superAdmin)'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
